import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { SwitchFieldComponent } from 'src/app/components/form/switch-field/switch-field.component';
import { RegulatorClientWebsite, RegulatorClient, RegulatorWebsitePayload } from 'src/app/models/entities/regulator';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { FormService } from 'src/app/services/form.service';
import { apiErrorMessage, isValidationError } from '../../regulators-ui.utils';

const CLIENT_OPTIONS_PAGE_SIZE = 1000;

/**
 * Create/update form of a regulator website (`POST|PUT /regulators/{regulatorId}/websites`).
 * Designed to be rendered inside a NgbModal template.
 */
@Component({
  selector: 'regulator-website-form',
  imports: [ReactiveFormsModule, InputFieldComponent, SelectFieldComponent, SwitchFieldComponent],
  templateUrl: './regulator-website-form.component.html',
  styleUrl: './regulator-website-form.component.scss'
})
export class RegulatorWebsiteFormComponent implements OnInit {
  @Input({ required: true }) regulatorId!: number;
  @Input() website: RegulatorClientWebsite | null = null;
  @Input() defaultClientId: number | null = null;
  @Output() saved = new EventEmitter<RegulatorClientWebsite | null>();
  @Output() cancelled = new EventEmitter<void>();

  form = new FormGroup({
    clientId: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    url: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    scanEnabled: new FormControl<boolean>(true, { nonNullable: true }),
  });

  clientOptions: SelectOption[] = [{ key: '', value: 'Select a client' }];
  isLoadingClients = false;
  isSaving = false;

  constructor(
    private regulatorsService: RegulatorsService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService
  ) { }

  ngOnInit(): void {
    const clientId = this.website?.clientId ?? this.defaultClientId;
    this.form.reset({
      clientId: clientId ? String(clientId) : '',
      url: this.website?.url ?? '',
      scanEnabled: this.website?.scanEnabled ?? true,
    });
    this.loadClients();
  }

  get isUpdate(): boolean {
    return !!this.website;
  }

  control(name: 'clientId' | 'url'): FormControl {
    return this.form.get(name) as FormControl;
  }

  onScanEnabledChange(value: boolean): void {
    this.form.controls.scanEnabled.setValue(value);
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSaving) {
      return;
    }

    const value = this.form.getRawValue();
    const payload: RegulatorWebsitePayload = {
      clientId: Number(value.clientId),
      url: value.url.trim(),
      scanEnabled: value.scanEnabled,
    };

    const request$ = this.website
      ? this.regulatorsService.updateWebsite(this.regulatorId, this.website.websiteId, payload)
      : this.regulatorsService.createWebsite(this.regulatorId, payload);

    this.isSaving = true;
    request$.subscribe({
      next: (response: any) => {
        this.isSaving = false;
        this.alertNotificationService.success('Website saved successfully');
        this.saved.emit(response?.data?.website ?? null);
      },
      error: (error: any) => {
        this.isSaving = false;
        if (isValidationError(error)) {
          this.formService.setValidationErrors(this.form, error.error.error.validationErrors);
        }
        this.alertNotificationService.error(apiErrorMessage(error, 'Error saving website'));
      }
    });
  }

  private loadClients(): void {
    this.isLoadingClients = true;
    const params = new HttpParams().set('page', 0).set('size', CLIENT_OPTIONS_PAGE_SIZE).set('sort', 'name,asc');
    this.regulatorsService.listClients(this.regulatorId, { params }).subscribe({
      next: (response: any) => {
        this.isLoadingClients = false;
        const clients: RegulatorClient[] = response?.data?.clients ?? [];
        this.clientOptions = [
          { key: '', value: 'Select a client' },
          ...clients.map(c => ({ key: String(c.clientId), value: c.code ? `${c.code} – ${c.name}` : c.name })),
        ];
      },
      error: () => {
        this.isLoadingClients = false;
        this.alertNotificationService.error('Error loading clients');
      }
    });
  }
}
